import { useState, useEffect, useRef } from 'react'
import s from './OpportunitiesInForecast.scss'
import {
  makeReportsPostRequest,
  makeGetRequest,
  getReportsBaseUrl,
  makePutRequest,
  makePatchRequest
} from 'utils/api'
import { toBarChartData, toUsers, getColors } from './serializers'
import { useLoading } from 'hooks'
import { getCurrency, getToken } from 'utils/authService'
import MaterialTable from '@material-table/core'
import { NumericFormat } from 'react-number-format'
import { timezoneDate, currencyFormatter, utcDate } from 'utils/helpers'
import Select from 'react-select'
import { FaInfoCircle, FaFileExcel, FaAngleRight, FaAngleDown } from 'react-icons/fa'
import { Card, CardBody, CardHeader, Collapse, UncontrolledTooltip, Button } from 'reactstrap'
import currency from 'currency.js'
import { AddButton } from 'components/common/AddButton'
import swal from 'sweetalert'
import { useDispatch } from 'react-redux'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { ExportCsv, ExportPdf } from '@material-table/exporters'
import { DatePicker, RangeDatePicker } from 'components/ideal-core-ui'
import { ResponsiveBar } from '@nivo/bar'
import isEmpty from 'lodash/isEmpty'

const OpportunitiesInForecast = () => {
  const [pipelineInfo, setPipelineInfo] = useState(null)
  const [result, setResult] = useState([])
  const [users, setUsers] = useState([])
  const [userFilter, setUserFilter] = useState([])
  const [startDate, setStartDate] = useState(undefined)
  const [endDate, setEndDate] = useState(undefined)
  const [, withLoadingLocks] = useLoading()
  const reduxDispatch = useDispatch()
  const [collapse, setCollapse] = useState('')

  const selectedOpportunities = useRef([])

  const setDateFilters = (dates, utc = true) => {
    if (!dates.from || !dates.to) {
      setStartDate(undefined)
      setEndDate(undefined)
      return
    }
    setStartDate(utc ? utcDate(dates.from) : dates.from)
    setEndDate(utc ? utcDate(dates.to) : dates.to)
  }

  const columns = [
    {
      title: 'Opp Name',
      field: 'name',
      width: '200px',
      sorting: true,
      editable: 'never',
      render: row => (
        <a href={`/opportunities/${row.dealId}`} target="_blank" rel="noreferrer">
          {row.name}
        </a>
      )
    },
    {
      title: 'Quote #',
      field: 'quoteNumber',
      width: '200px',
      editable: 'never',
      sorting: true
    },
    {
      title: 'Geographical Area',
      field: 'geographicalAreaName',
      width: '200px',
      editable: 'never',
      sorting: true
    },
    {
      title: `${getCurrency().symbol} in Forecast`,
      field: 'forecastRevenue',
      width: '200px',
      sorting: true,
      defaultSort: 'desc',
      editable: 'never',
      render: row => (
        <NumericFormat
          thousandSeparator
          decimalScale={2}
          value={row.forecastRevenue}
          prefix={getCurrency().symbol}
          displayType="text"
        />
      ),
      exportTransformer: row => currencyFormatter({ value: row.forecastRevenue })
    },
    {
      title: '% in Forecast',
      field: 'closingChance',
      sorting: true,
      width: '200px',
      render: row => `${row.closingChance}%`,
      exportTransformer: row => `${row.closingChance}%`
    },
    {
      title: 'Bid Value',
      field: 'value',
      width: '200px',
      sorting: true,
      editable: 'never',
      render: row => (
        <NumericFormat
          thousandSeparator
          decimalScale={2}
          value={row.value}
          prefix={getCurrency().symbol}
          displayType="text"
        />
      ),
      exportTransformer: row => currencyFormatter({ value: row.value })
    },
    {
      title: 'Work type',
      field: 'dealWorkTypeName',
      sorting: true,
      width: '200px',
      editable: 'never'
    },
    {
      title: 'Expected Close Date',
      field: 'expectedCloseDate',
      sorting: true,
      width: '200px',
      render: row => (row.expectedCloseDate ? timezoneDate(row.expectedCloseDate) : null),
      exportTransformer: row =>
        row.expectedCloseDate ? timezoneDate(row.expectedCloseDate) : null,
      emptyValue: '(empty)',
      editComponent: props => {
        return (
          <DatePicker
            keyProp={props.rowData.dealId}
            value={props.rowData.expectedCloseDate && timezoneDate(props.rowData.expectedCloseDate)}
            onChange={async value => {
              const date = value && value !== null ? utcDate(value) : ''
              await makePatchRequest(`/v1/deals/${props.rowData.dealId}`, {
                expectedCloseDate: date
              })
              refreshReport()
            }}
            placeholder="Select Expected Close Date"
          />
        )
      }
    },
    {
      title: 'Project Start Date',
      field: 'projectStartDate',
      sorting: true,
      width: '200px',
      render: row => (row.projectStartDate ? timezoneDate(row.projectStartDate) : null),
      exportTransformer: row => (row.projectStartDate ? timezoneDate(row.projectStartDate) : null),
      emptyValue: '(empty)',
      editComponent: props => {
        return (
          <DatePicker
            keyProp={props.rowData.dealId}
            value={props.rowData.projectStartDate && timezoneDate(props.rowData.projectStartDate)}
            onChange={async value => {
              const date = value && value !== null ? utcDate(value) : ''
              await makePatchRequest(`/v1/deals/${props.rowData.dealId}`, {
                dealProjectStartDate: date
              })
              refreshReport()
            }}
            placeholder="Select Project Start Date"
          />
        )
      }
    },
    {
      title: 'Project End Date',
      field: 'projectEndDate',
      sorting: true,
      width: '200px',
      render: row => (row.projectEndDate ? timezoneDate(row.projectEndDate) : null),
      exportTransformer: row => (row.projectEndDate ? timezoneDate(row.projectEndDate) : null),
      emptyValue: '(empty)',
      editComponent: props => {
        return (
          <DatePicker
            keyProp={props.rowData.dealId}
            value={props.rowData.projectEndDate && timezoneDate(props.rowData.projectEndDate)}
            onChange={async value => {
              const date = value && value !== null ? utcDate(value) : ''
              await makePatchRequest(`/v1/deals/${props.rowData.dealId}`, {
                dealProjectEndDate: date
              })
              refreshReport()
            }}
            placeholder="Select Project End Date"
          />
        )
      }
    },
    {
      title: 'Cost',
      field: 'cost',
      width: '200px',
      sorting: true,
      editable: 'never',
      render: row => (
        <NumericFormat
          thousandSeparator
          decimalScale={2}
          value={row.cost}
          prefix={getCurrency().symbol}
          displayType="text"
        />
      ),
      exportTransformer: row => currencyFormatter({ value: row.cost })
    },
    {
      title: 'Profit',
      field: 'grossProfit',
      width: '200px',
      sorting: true,
      editable: 'never',
      render: row => (
        <NumericFormat
          thousandSeparator
          decimalScale={2}
          value={row.grossProfit}
          prefix={getCurrency().symbol}
          displayType="text"
        />
      ),
      exportTransformer: row => currencyFormatter({ value: row.grossProfit })
    },
    {
      title: 'Gross Margin',
      field: 'margin',
      sorting: true,
      width: '200px',
      editable: 'never',
      render: row => `${row.margin}%`,
      exportTransformer: row => `${row.margin}%`
    },
    {
      title: 'Markup',
      field: 'markup',
      sorting: true,
      width: '200px',
      editable: 'never',
      render: row => `${row.markup}%`,
      exportTransformer: row => `${row.markup}%`
    },
    {
      title: `${getCurrency().symbol} in Profit`,
      field: 'forecastGrossProfit',
      width: '200px',
      sorting: true,
      editable: 'never',
      render: row => (
        <NumericFormat
          thousandSeparator
          decimalScale={2}
          value={row.forecastGrossProfit}
          prefix={getCurrency().symbol}
          displayType="text"
        />
      ),
      exportTransformer: row => currencyFormatter({ value: row.forecastGrossProfit })
    },
    {
      title: 'Opp Status',
      field: 'status',
      width: '200px',
      sorting: true,
      editable: 'never'
    },
    {
      title: 'Client(s)',
      field: 'clients',
      sorting: true,
      width: '200px',
      editable: 'never'
    },
    {
      title: 'Contact(s)',
      field: 'contacts',
      sorting: true,
      editable: 'never',
      width: '400px'
    },
    {
      title: 'Awarded To',
      field: 'awardedTo',
      width: '200px',
      editable: 'never',
      sorting: true
    },
    {
      title: 'Bid Due Date',
      field: 'dueDate',
      sorting: true,
      editable: 'never',
      width: '200px',
      render: row => (row.dueDate ? timezoneDate(row.dueDate) : null),
      exportTransformer: row => (row.dueDate ? timezoneDate(row.dueDate) : null)
    },
    {
      title: 'Bid Follow-up Date',
      field: 'followUpDate',
      sorting: true,
      width: '200px',
      editable: 'never',
      render: row => (row.followUpDate ? timezoneDate(row.followUpDate) : null),
      exportTransformer: row => (row.followUpDate ? timezoneDate(row.followUpDate) : null)
    },
    {
      title: 'Opp Manager',
      field: 'manager',
      width: '200px',
      editable: 'never',
      sorting: true
    },
    {
      title: 'Project #',
      field: 'projectNumber',
      width: '200px',
      editable: 'never',
      sorting: true
    }
  ]

  useEffect(() => {
    ;(async () => {
      const { content } = await makeGetRequest('/v1/users')
      setUsers(toUsers(content))
    })()
  }, [])

  async function refreshReport() {
    const result = await withLoadingLocks(
      makeReportsPostRequest('/v1/sales/ytd/oppt/forecast', {
        from: startDate,
        to: endDate,
        ...(userFilter && userFilter.length
          ? { userFilter: userFilter.map(user => user.value) }
          : {})
      })
    )
    setResult(result)
    setPipelineInfo(toBarChartData(result))
  }

  useEffect(() => {
    refreshReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilter, startDate, endDate])

  const onBtnExport = () => {
    window.open(
      getReportsBaseUrl(
        `/v1/sales/ytd/oppt/forecast/export?from=${startDate}&to=${endDate}&userFilter=${userFilter
          .map(user => user.value)
          .join()}&token=${getToken()}`
      ),
      '_blank'
    )
  }

  const onSelectionChange = rows => {
    selectedOpportunities.current = rows
  }

  const removeOpportunities = async () => {
    if (selectedOpportunities.current.length === 0) {
      await swal({
        title: 'No opportunities selected',
        text: 'Please select at least one opportunity to remove from Forecast.',
        icon: 'warning'
      })
      return
    }
    const willRemove = await swal({
      title: 'Are you sure you want to remove the selected opportunities from Forecast?',
      text: 'Please note: If you remove them and decide to add them back in later, you can do so from each opportunity page.',
      buttons: ['No', 'Yes'],
      icon: 'warning'
    })
    if (willRemove) {
      try {
        const dealIds = selectedOpportunities.current.map(opportunity => opportunity.dealId)
        await makePutRequest('/v1/deals/disableAddToForecastReportsForMultipleDeals', {
          dealIds
        })
        refreshReport()
        reduxDispatch({
          type: PUSH_NOTIFICATION,
          payload: {
            title: 'Opportunities removed from Forecast',
            level: 'success'
          }
        })
      } catch (e) {
        reduxDispatch({
          type: PUSH_NOTIFICATION,
          payload: {
            title: 'There was a problem removing the opportunities from Forecast.',
            level: 'error'
          }
        })
      }
    }
  }

  const toggleCollapse = month => {
    selectedOpportunities.current = []
    if (collapse !== month) {
      setCollapse(month)
    } else {
      setCollapse('')
    }
  }

  return (
    <>
      <h6 className={s.title}>Sales Reports &gt; Opportunities in Forecast</h6>
      <div className={s.filters}>
        <RangeDatePicker
          startDate={startDate}
          endDate={endDate}
          onChange={setDateFilters}
          clearable
        />
        <Select
          onChange={values => setUserFilter(values)}
          options={users}
          value={userFilter}
          placeholder="Select User(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type1" style={{ position: 'relative', top: 15, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type1">
          To see all opportunities, please remove all users from selection.
        </UncontrolledTooltip>
      </div>
      {Object.keys(result).length > 0 && !isEmpty(pipelineInfo) ? (
        <>
          <h6 className={s.subtitle}>
            Total: {pipelineInfo?.totalCount ?? 0} opportunities &#8226; Bid:{' '}
            {currency(pipelineInfo?.totalBidValue ?? 0, { symbol: getCurrency().symbol }).format()}
          </h6>
          <div
            className={s.barChart}
            style={{ height: pipelineInfo.barByValue.length * 60 + 'px', maxHeight: 800 }}
          >
            <ResponsiveBar
              colors={getColors}
              data={pipelineInfo.barByValue}
              keys={pipelineInfo.keys}
              indexBy="month"
              enableGridX
              enableGridY
              margin={{ top: 10, right: 100, bottom: 50, left: 100 }}
              padding={0.2}
              innerPadding={3}
              groupMode="grouped"
              borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legendPosition: 'middle',
                legendOffset: 32
              }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 5,
                tickRotation: 0,
                legendPosition: 'middle',
                legendOffset: -40,
                format: value => currency(value, { symbol: getCurrency().symbol }).format()
              }}
              tooltip={({ id, value }) => (
                <span>
                  {id}:{' '}
                  <strong>
                    <NumericFormat
                      thousandSeparator
                      decimalScale={2}
                      value={value}
                      prefix={getCurrency().symbol}
                      displayType="text"
                    />
                  </strong>
                </span>
              )}
              enableLabel={false}
              labelSkipWidth={12}
              labelSkipHeight={9}
              labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              legends={[
                {
                  dataFrom: 'keys',
                  anchor: 'bottom-right',
                  direction: 'column',
                  justify: false,
                  translateX: 120,
                  translateY: 0,
                  itemsSpacing: 14,
                  itemWidth: 100,
                  itemHeight: 20,
                  itemDirection: 'left-to-right',
                  itemOpacity: 0.85,
                  symbolSize: 20,
                  effects: [
                    {
                      on: 'hover',
                      style: {
                        itemOpacity: 1
                      }
                    }
                  ]
                }
              ]}
              animate
              motionStiffness={90}
              motionDamping={15}
            />
          </div>
          <div className={s.table}>
            <AddButton className={s.exportExcel} onClick={onBtnExport}>
              <FaFileExcel className={s.excelIcon} />
              Export to Excel
            </AddButton>
            {Object.keys(result).map(month => (
              <Card className={s.card} key={month}>
                <CardHeader onClick={() => toggleCollapse(month)} data-event={month}>
                  <span className={s.arrow}>
                    {collapse === month ? <FaAngleRight /> : <FaAngleDown />}
                  </span>
                  {month} ({result[month].count}) &#8226; Bid:{' '}
                  {currency(result[month].totalBidValue, { symbol: getCurrency().symbol }).format()}{' '}
                  &#8226; Cost:{' '}
                  {currency(result[month].totalCostValue, {
                    symbol: getCurrency().symbol
                  }).format()}{' '}
                  &#8226; Profit:{' '}
                  {currency(result[month].totalProfitValue, {
                    symbol: getCurrency().symbol
                  }).format()}
                </CardHeader>
                <Collapse isOpen={collapse === month}>
                  <CardBody>
                    {collapse === month && (
                      <>
                        <Button className={s.saveBtn} onClick={removeOpportunities}>
                          Remove from Forecast
                        </Button>
                        <MaterialTable
                          title=""
                          columns={columns}
                          data={result[month].deals}
                          options={{
                            tableLayout: 'fixed',
                            sorting: true,
                            headerStyle: {
                              backgroundColor: '#eee',
                              color: '#000',
                              fontSize: '14px'
                            },
                            pageSize: 10,
                            padding: 'dense',
                            pageSizeOptions: [
                              5,
                              10,
                              20,
                              50,
                              { value: result.length, label: 'All' }
                            ],
                            emptyRowsWhenPaging: false,
                            exportMenu: [
                              {
                                label: 'Export PDF',
                                exportFunc: (cols, datas) =>
                                  ExportPdf(cols, datas, 'Opportunities in Forecast')
                              },
                              {
                                label: 'Export CSV',
                                exportFunc: (cols, datas) =>
                                  ExportCsv(cols, datas, 'Opportunities in Forecast')
                              }
                            ],
                            columnsButton: true,
                            selection: true,
                            showTextRowsSelected: false
                          }}
                          style={{
                            boxShadow: 'none',
                            fontSize: '13px'
                          }}
                          cellEditable={{
                            onCellEditApproved: async (newValue, oldValue, rowData) => {
                              if (rowData.tableData.editCellList[0].field === 'expectedCloseDate')
                                return new Promise(resolve => resolve())

                              await makePatchRequest(`/v1/deals/${rowData.dealId}`, {
                                closingChance: newValue
                              })
                              refreshReport()
                            }
                          }}
                          onSelectionChange={onSelectionChange}
                        />
                      </>
                    )}
                  </CardBody>
                </Collapse>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <p className={s.noResults}>No results to display. Try different filters.</p>
      )}
    </>
  )
}

export default OpportunitiesInForecast
